import { CopilotConversation, PromptResponseCitation } from 'models/copilot/copilotConversation';
import { TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';

// Mirror of ProcessStateSupplierCompliance.SupplierComplianceStage (isoplanner-api, PLAN-073)
export enum SupplierComplianceStage {
  Investigating = 0,
  Summary = 1,
  Staging = 2,
  Checking = 3,
  Review = 4,
  Completed = 5,
  // waiting stage (PLAN-CTI-084): the card that asks the supplier for a certificate
  RequestCard = 6,
  // waiting stages (PLAN-CTI-092): the downloads the run could not decide about, and the missing Asset documents location
  FilesCard = 8,
  LocationCard = 9,
}

// Mirror of SupplierComplianceDelta (isoplanner-api): a standard compared with the previous run
export enum SupplierComplianceDelta {
  New = 0,
  Same = 1,
  Expired = 2,
  NoLongerPublished = 3,
  Renewed = 4,
  // found by the previous run, not by this one while one of its searches did not finish: the previous entry is kept
  NotChecked = 5,
}

// Mirror of SupplierComplianceStatus (isoplanner-api): the overall result of a run
export enum SupplierComplianceStatus {
  Compliant = 0,
  Partial = 1,
  NotFound = 2,
}

// Mirror of SupplierDocumentVerdict (isoplanner-api)
export enum SupplierDocumentVerdict {
  Match = 0,
  Mismatch = 1,
  Unreadable = 2,
}

// Mirror of SupplierDocumentDecision (isoplanner-api)
export enum SupplierDocumentDecision {
  Pending = 0,
  Save = 1,
  Skip = 2,
  // the check could not decide; the person decides on the files card (PLAN-CTI-092)
  Undecided = 3,
}

// Mirror of SupplierComplianceFileOrigin (isoplanner-api, PLAN-CTI-092): where a file the check looked at came from
export enum SupplierComplianceFileOrigin {
  Downloaded = 0,
  Linked = 1,
  Uploaded = 2,
  Picked = 3,
  Dropped = 4,
}

// Mirror of SupplierComplianceFileVerdict (isoplanner-api, PLAN-CTI-092): how a file stands against the report
export enum SupplierComplianceFileVerdict {
  Match = 0,
  Mismatch = 1,
  Unreadable = 2,
  Duplicate = 3,
  Same = 4,
  Older = 5,
  Superseded = 6,
  TooLarge = 7,
}

// One certification or attestation the supplier publishes; fileUrl only for an https PDF or image
export class SupplierComplianceStandard {
  code: string = '';

  status: string = '';

  issuer?: string;

  validFrom?: Date;

  validTo?: Date;

  citations: PromptResponseCitation[] = [];

  fileUrl?: string;

  delta: SupplierComplianceDelta = SupplierComplianceDelta.New;
}

// The previous run's outcome, which the next run compares with
export class SupplierComplianceBaseline {
  investigated?: Date;

  standards: SupplierComplianceStandard[] = [];

  savedLinkIds: number[] = [];

  status: SupplierComplianceStatus = SupplierComplianceStatus.NotFound;
}

// A document the asset already holds in its Asset documents folder
export class SupplierComplianceDocument {
  linkId: number = 0;

  name: string = '';

  version?: string;

  modified?: Date;
}

// The model's check of one staged file; reasons explain a mismatch
export class SupplierDocumentCheck {
  verdict: SupplierDocumentVerdict = SupplierDocumentVerdict.Unreadable;

  issuer?: string;

  standard?: string;

  scope?: string;

  subject?: string;

  validFrom?: Date;

  validTo?: Date;

  reasons: string[] = [];
}

// Where a staged file would land in the asset's folder and whether it replaces a version there
export class SupplierSavePlan {
  folderPath: string = '';

  fileName: string = '';

  existingLinkId?: number;

  existingVersion?: string;

  warnings: string[] = [];
}

// A downloaded file in the session's container: its File block, its source, its check and the user's decision
export class SupplierStagedFile {
  index: number = 0;

  blockId: string = '';

  tempBlobName: string = '';

  fileName: string = '';

  contentType: string = '';

  size: number = 0;

  sha256: string = '';

  sourceUrl: string = '';

  standardCode: string = '';

  check?: SupplierDocumentCheck;

  decision: SupplierDocumentDecision = SupplierDocumentDecision.Pending;

  origin: SupplierComplianceFileOrigin = SupplierComplianceFileOrigin.Downloaded;

  // how the reconcile placed the file against the report; undefined until it is placed
  fileVerdict?: SupplierComplianceFileVerdict;

  savedLinkId?: number;

  savedVersion?: string;

  // when the run saved the file; undefined for a file that was not saved
  savedOn?: Date;

  savePlan?: SupplierSavePlan;

  // the refusal of the plan or the save (a code such as OUTPUTLOC0007); the file stays Pending
  saveError?: string;
}

// The compliance investigation of one Supplier asset as an AI process (PLAN-073), mirror of
// ProcessStateSupplierCompliance: the previous outcome, the standards with their deltas, the staged files and the
// assessment task that started the run, if any
export class TenantProcessStateSupplierCompliance {
  status: TenantProcessStatus = TenantProcessStatus.NotStarted;

  assetId: number = 0;

  assetName: string = '';

  stage: SupplierComplianceStage = SupplierComplianceStage.Investigating;

  previousOutcome?: SupplierComplianceBaseline;

  existingDocuments: SupplierComplianceDocument[] = [];

  standards: SupplierComplianceStandard[] = [];

  summaryMarkdown?: string;

  stagedFiles: SupplierStagedFile[] = [];

  consumerTaskId?: number;

  // the run's own Copilot session, made at Staging: the staged File blocks live in it
  sessionId?: number;

  conversation: CopilotConversation = new CopilotConversation();
}
